import React, { memo, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import moment from "moment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreativesBase from "../creatives/Creatives";
import LocationDateBase from "./LocationDate";
import LocationBudgetBase from "./LocationBudget";
import LocationHeaderBase from "./LocationHeader";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import LocationBrandMatchBase from "./LocationBrandMatch";
import { getDigitalCampaignLocationDisplayName } from "@/features/campaigns/digital/launch-digital-campaign/utils/locationDisplayName";
import {
  buildBudgetRecommendationState,
  getBudgetRecommendationForBudget,
} from "@/features/campaigns/shared/helpers/budgetRecommendationHelper";
import {
  getPackageChannelOptions,
  isParticipantChannelSelectionEnabled,
  pruneSelectedChannelsForPackages,
  shouldPruneChannelsForPackageChange,
} from "@/features/campaigns/shared/helpers/channelSelectionHelper";
import {
  canShowImpressionEstimation,
  getImpressionEstimation,
  getEstimatedImpressions,
} from "@/features/campaigns/shared/helpers/estimatedImpressionsHelper";

type LocationId = string | number;

type LocationFormData = {
  start_date?: string | null;
  end_date?: string | null;
  creatives?: LocationId[];
  channels?: LocationId[];
  advance_setting?: unknown;
  budget?: string | number | null;
  brand_contribution_amount?: unknown;
  [key: string]: unknown;
};

type LocationProps = {
  campaign?: Record<string, any>;
  loc: {
    id: LocationId;
    location_name?: string;
    creatives?: any[];
    isPastDue?: boolean;
    is_creative?: boolean;
    budget?: {
      brand_match_preset_amount?: unknown;
    };
    [key: string]: unknown;
  };
  locFormData?: LocationFormData;
  setFieldValue: (_field: LocationId, _value: LocationFormData) => void;
  setFieldError?: (..._args: unknown[]) => void;
  setExpanded: (_updater: (_expanded: LocationId[]) => LocationId[]) => void;
  productList?: unknown;
  getAdvanceSettingData?: unknown;
  isUnsubscribed?: boolean;
  subscriptionStatusType?: unknown;
  unCheckedLocations: (_locationId?: LocationId) => void;
  shouldExpandIt: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onGetBudgetRecommendation?: (_request: {
    campaign?: Record<string, any>;
    loc: LocationProps["loc"];
    locFormData: LocationFormData;
  }) => Promise<unknown>;
  isBudgetRecommendationEnabled?: boolean;
};

const Creatives = CreativesBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const LocationDate = LocationDateBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const LocationHeader = LocationHeaderBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const LocationBudget = LocationBudgetBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const LocationBrandMatch =
  LocationBrandMatchBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const Location = ({
  campaign,
  loc,
  locFormData = {},
  setFieldValue,
  setFieldError,
  setExpanded,
  productList,
  getAdvanceSettingData,
  isUnsubscribed = false,
  subscriptionStatusType,
  unCheckedLocations,
  shouldExpandIt,
  onBlur,
  onFocus,
  onGetBudgetRecommendation,
  isBudgetRecommendationEnabled = false,
}: LocationProps) => {
  const [localBudgetRecommendationError, setLocalBudgetRecommendationError] =
    useState("");

  const clearBudgetRecommendationError = () => {
    setLocalBudgetRecommendationError("");
  };

  const getBudgetRecommendationErrorMessage = (error) => {
    return (
      error?.response?.data?.message ||
      error?.data?.message ||
      error?.message ||
      "Budget recommendation is not available for this location."
    );
  };

  const handleOnExpand = (panel) => (event, isExpanded) => {
    if (isExpanded) {
      setExpanded((expanded) => [...expanded, panel]);
    } else {
      setExpanded((expanded) => expanded.filter((l) => l !== panel));
      onBlur?.();
    }
  };

  const locName = useMemo(
    () => getDigitalCampaignLocationDisplayName(loc),
    [loc]
  );

  const minMaxRequired = useMemo(() => {
    return joinCampaignHelper.getRequiredMinMax(locFormData, campaign);
  }, [campaign, locFormData]);

  const canGetBudgetRecommendation = Boolean(
    locFormData?.start_date &&
      locFormData?.end_date &&
      locFormData?.creatives?.length
  );

  const selectedPackages = useMemo(() => {
    const selectedCreativeIds = new Set(
      (locFormData.creatives || []).map((creativeId) => String(creativeId))
    );
    return (loc.creatives || []).filter((creative) =>
      selectedCreativeIds.has(String(creative.creative_id))
    );
  }, [loc.creatives, locFormData.creatives]);

  const estimatedChannels = useMemo(() => {
    const packageChannels = getPackageChannelOptions(selectedPackages);
    return getImpressionEstimation(campaign)
      ?.canParticipantSelectSpecificChannels
      ? packageChannels.filter((channel) =>
          (locFormData.channels || []).some(
            (channelId) => String(channelId) === String(channel.id)
          )
        )
      : packageChannels;
  }, [campaign, selectedPackages, locFormData.channels]);

  const estimatedImpressions = useMemo(() => {
    return getEstimatedImpressions({
      budget: locFormData.budget as string | number | null,
      brandContribution: locFormData.brand_contribution_amount as
        | string
        | number
        | null,
      impressionEstimation: getImpressionEstimation(campaign),
      channels: estimatedChannels,
      restrictToChannels: true,
    });
  }, [
    campaign,
    estimatedChannels,
    locFormData.budget,
    locFormData.brand_contribution_amount,
  ]);

  const getBudgetBrandContributionAmount = (budget: any) => {
    const brandContributionAmount =
      joinCampaignHelper.getBrandContributionAmount({
        amount: budget * 1,
        brand_contribution: campaign?.brand_contribution,
        brand_contribution_type: campaign?.brand_contribution_type,
        contribution_percentage: campaign?.contribution_percentage,
        contribution_max_limit: campaign?.contribution_max_limit,
        brand_match_preset_amount:
          (loc?.budget?.brand_match_preset_amount as any) * 1 || 0,
      });
    const isInvalidBudget = joinCampaignHelper.getRequiredMinMax(
      { ...locFormData, budget },
      campaign
    );

    return budget && isInvalidBudget ? 0 : Number(brandContributionAmount);
  };

  const handleOnSelectCreative = (selectedCreatives) => {
    clearBudgetRecommendationError();
    const oldVal = { ...locFormData };
    const nextVal = {
      ...oldVal,
      creatives: selectedCreatives,
      isBudgetRecommendationLoading: false,
      budgetRecommendationError: null,
    };

    if (
      isParticipantChannelSelectionEnabled(campaign) &&
      shouldPruneChannelsForPackageChange({
        previousPackageIds: oldVal.creatives,
        nextPackageIds: selectedCreatives,
      })
    ) {
      nextVal.channels = pruneSelectedChannelsForPackages({
        creatives: loc.creatives,
        selectedPackageIds: selectedCreatives,
        selectedChannelIds: oldVal.channels,
      });
    }

    setFieldValue(loc.id, {
      ...nextVal,
      budgetRecommendation: getBudgetRecommendationForBudget(
        nextVal.budget,
        nextVal
      ),
    });
    unCheckedLocations(loc?.id);
  };

  const handleOnSelectChannels = (selectedChannels) => {
    clearBudgetRecommendationError();
    const oldVal = { ...locFormData };
    const nextVal = {
      ...oldVal,
      channels: selectedChannels,
      isBudgetRecommendationLoading: false,
      budgetRecommendationError: null,
    };
    setFieldValue(loc.id, {
      ...nextVal,
      budgetRecommendation: getBudgetRecommendationForBudget(
        nextVal.budget,
        nextVal
      ),
    });
    unCheckedLocations(loc?.id);
  };

  // to get advance settings data
  const handleOnFillSettingData = (selectedSettingsData: unknown) => {
    const oldVal = { ...locFormData };
    setFieldValue(loc.id, {
      ...oldVal,
      advance_setting: selectedSettingsData,
    });
  };
  const handleOnEnterBudget = (enteredBudget) => {
    clearBudgetRecommendationError();
    const oldVal = { ...locFormData };
    const budgetRecommendation = getBudgetRecommendationForBudget(
      enteredBudget,
      oldVal
    );

    setFieldValue(loc.id, {
      ...oldVal,
      budget: enteredBudget,
      budgetRecommendation,
      isBudgetRecommendationLoading: false,
      budgetRecommendationError: null,
      brand_contribution_amount:
        getBudgetBrandContributionAmount(enteredBudget),
    });
    unCheckedLocations(loc?.id);
  };

  const getRecommendationData = (response) => {
    return response?.data?.data || response?.data || response;
  };

  const handleOnGetBudgetRecommendation = async () => {
    clearBudgetRecommendationError();
    const oldVal = { ...locFormData };

    setFieldValue(loc.id, {
      ...oldVal,
      isBudgetRecommendationLoading: true,
      budgetRecommendationError: null,
    });

    try {
      const recommendationResponse = await onGetBudgetRecommendation?.({
        campaign,
        loc,
        locFormData,
      });
      const recommendation = getRecommendationData(recommendationResponse);
      const recommendedBudget = recommendation?.finalRecommendation;

      if (recommendedBudget === undefined || recommendedBudget === null) {
        const errorMessage =
          "Budget recommendation is not available for this location.";
        setLocalBudgetRecommendationError(errorMessage);
        setFieldValue(loc.id, {
          ...oldVal,
          isBudgetRecommendationLoading: false,
          budgetRecommendationError: errorMessage,
        });
        return;
      }

      setFieldValue(loc.id, {
        ...oldVal,
        budget: recommendedBudget,
        ...buildBudgetRecommendationState({
          locFormData: { ...oldVal, budget: recommendedBudget },
          recommendation,
        }),
        isBudgetRecommendationLoading: false,
        budgetRecommendationError: null,
        brand_contribution_amount:
          getBudgetBrandContributionAmount(recommendedBudget),
      });
      unCheckedLocations(loc?.id);
    } catch (error) {
      const errorMessage = getBudgetRecommendationErrorMessage(error);
      setLocalBudgetRecommendationError(errorMessage);
      setFieldValue(loc.id, {
        ...oldVal,
        isBudgetRecommendationLoading: false,
        budgetRecommendationError: errorMessage,
      });
    }
  };

  const handleOnSelectStartDate = (d) => {
    clearBudgetRecommendationError();
    const oldVal = { ...locFormData };
    const newVal = {
      ...oldVal,
      start_date: moment(d).isValid() ? moment(d).format("YYYY-MM-DD") : null,
      isBudgetRecommendationLoading: false,
      budgetRecommendationError: null,
    };
    setFieldValue(loc.id, {
      ...newVal,
      budgetRecommendation: getBudgetRecommendationForBudget(
        newVal.budget,
        newVal
      ),
    });
    unCheckedLocations(loc?.id);
  };

  const handleOnSelectEndDate = (d, startDate) => {
    clearBudgetRecommendationError();
    const oldVal = { ...locFormData };
    const newVal = {
      ...oldVal,
      start_date: startDate ? startDate : oldVal?.start_date,
      end_date: moment(d).isValid() ? moment(d).format("YYYY-MM-DD") : null,
      isBudgetRecommendationLoading: false,
      budgetRecommendationError: null,
    };
    setFieldValue(loc.id, {
      ...newVal,
      budgetRecommendation: getBudgetRecommendationForBudget(
        newVal.budget,
        newVal
      ),
    });
    unCheckedLocations(loc?.id);
  };

  return (
    <Accordion
      expanded={shouldExpandIt}
      onChange={handleOnExpand(loc.id)}
      className={`location ${loc?.isPastDue ? "past_due" : ""}`}
      onFocus={() => {
        onFocus?.();
      }}
      onBlur={() => {
        onBlur?.();
      }}
    >
      <AccordionSummary
        expandIcon={
          <ChevronRightIcon
            style={{
              transform: shouldExpandIt ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        }
        aria-controls={`panel${loc.id}-content`}
        id={`panel${loc.id}-header`}
        className="location-header"
      >
        <LocationHeader
          loc={loc}
          locFormData={locFormData}
          locName={locName}
          campaign={campaign}
          isUnsubscribed={isUnsubscribed}
          isCreative={loc?.is_creative}
          subscriptionStatusType={subscriptionStatusType}
        />
      </AccordionSummary>
      <AccordionDetails className="location-details">
        <div className="card w-100">
          <div className="card-body">
            <Creatives
              creatives={loc.creatives}
              onSelectCreative={handleOnSelectCreative}
              initiallySelected={locFormData.creatives}
              locationName={loc?.location_name}
              loc={loc}
              campaign={campaign}
              productList={productList}
              getAdvanceSettingData={getAdvanceSettingData}
              onFillSettingsData={handleOnFillSettingData}
              initialAnswers={locFormData.advance_setting}
              initialDisabled={isUnsubscribed}
              selectedChannelIds={locFormData.channels}
              onSelectChannels={handleOnSelectChannels}
            />
          </div>
          <div
            className="card-footer bg-white pb-3"
            role="group"
            aria-label="Location campaign dates and budget"
          >
            <div className="card border-0">
              <div className="card-body px-0 py-0">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-5 py-2">
                      <LocationDate
                        // key={`${loc.id}-${locFormData?.start_date}-${locFormData?.end_date}`}
                        key={`${loc.id}-${locFormData?.start_date}`}
                        onSelectStartDate={handleOnSelectStartDate}
                        onSelectEndDate={handleOnSelectEndDate}
                        start_date={locFormData?.start_date}
                        end_date={locFormData?.end_date}
                        campaign={campaign}
                        setFieldError={setFieldError}
                        loc={loc}
                        canChangeDate={joinCampaignHelper.canChangeDate(
                          campaign
                        )}
                        initialDisabled={isUnsubscribed}
                      />
                    </div>
                    <div className="col-lg-7 location-budget-input-container">
                      <div className="row">
                        <div className="col-md">
                          <div
                            className="location-budget-box align-with-location-date py-2"
                            role="group"
                            aria-label="Location budget controls"
                          >
                            <LocationBudget
                              // key={`${loc.id}-${locFormData?.budget}`}
                              onEnterBudget={handleOnEnterBudget}
                              budget={locFormData.budget}
                              budgetRecommendation={
                                locFormData.budgetRecommendation
                              }
                              marketData={loc}
                              locFormData={locFormData}
                              minMaxRequired={minMaxRequired}
                              canGetRecommendation={canGetBudgetRecommendation}
                              isBudgetRecommendationAvailable={
                                isBudgetRecommendationEnabled
                              }
                              isGettingRecommendation={
                                locFormData.isBudgetRecommendationLoading
                              }
                              recommendationError={
                                locFormData.budgetRecommendationError ||
                                localBudgetRecommendationError
                              }
                              onGetRecommendation={
                                handleOnGetBudgetRecommendation
                              }
                              campaignType={campaign?.budget_type}
                              estimatedImpressions={estimatedImpressions}
                              impressionEstimationEnabled={canShowImpressionEstimation(
                                campaign
                              )}
                              hasSelectedPackage={selectedPackages.length > 0}
                              hasSelectedChannel={estimatedChannels.length > 0}
                              // ability_to_set_budget={
                              //   campaign?.ability_to_set_budget
                              // }
                              // canChangeBudget={joinCampaignHelper.canChangeBudget(
                              //   campaign
                              // )}
                              initialDisabled={isUnsubscribed}
                            />
                          </div>
                        </div>
                        {campaign?.brand_contribution === 1 && (
                          <div className="col-md">
                            <div
                              className="location-budget-box align-with-location-date border-0 py-2"
                              role="group"
                              aria-label="Brand match controls"
                            >
                              <LocationBrandMatch
                                contribution={
                                  locFormData?.brand_contribution_amount
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default memo(Location);
